<template v-if="items && items.length > 0">
	<section class="promotions">
		<div v-for="block in items.slice(0, amount)" :key="block.ID" class="promoblock">
			<div class="header-container">
				<div class="content">
					<h2>{{ block.header }}</h2>
				</div>
			</div>
			<picture>
				<source v-if="block.imageWebp" :srcset="block.image" type="image/webp" />
				<source :srcset="block.image" />
				<img :src="block.image" :alt="block.imageAlt" class="promo-img" loading="lazy" />
			</picture>
			<div>
				<div v-parse-links class="content-below" v-html="block.content" />
			</div>
		</div>
	</section>
</template>

<script setup>
defineProps({
	items: { type: Array, default: () => [] },
	amount: { type: Number, default: 3 },
});
</script>

<style lang="scss" scoped>
.promotions {
	width: 100%;
	max-width: 1680px;
	margin: 0 auto 20px;
	padding: 0 25px;
	text-align: center;
	display: flex;
	flex-flow: row wrap;
	gap: 10px;

	.promoblock {
		flex-basis: calc(100% / 3 - 25px);
		text-decoration: none;
		margin: 0 auto;
		position: relative;
		display: flex;
		flex-direction: column;
		overflow: hidden;

		picture {
			overflow: hidden;
		}

		.header-container {
			position: absolute;
			aspect-ratio: 1;
			top: 0;
			width: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.content {
			position: relative;
			text-align: center;
			color: #fff;
			z-index: 2;
			margin-top: 15px;

			h2,
			h3,
			h2 a,
			h3 a {
				color: var(--tertiary-background-color);
				margin: 0 0 30px;
				transition: all 0.3s ease-in-out;
			}
		}

		&:hover .content {
			h2,
			h3,
			h2 a,
			h3 a {
				color: #fff !important;
			}
		}

		.promo-img {
			opacity: 0.75;
			width: 100%;
			transition: all 0.3s ease-in-out;
		}

		.content-below {
			flex-grow: 1;
			display: flex;
			flex-direction: row;
			justify-content: space-between;
			position: relative;
			margin-top: 10px;
			width: 100%;
			left: 0;
			right: 0;
			text-align: left;
			color: var(--body-color);
			z-index: 2;

			:deep(h2),
			:deep(h3),
			:deep(h2 a),
			:deep(h3 a) {
				color: var(--body-color);
				margin: 0 0 30px;
			}

			:deep(p:first-child) {
				width: 68%;
				margin-bottom: 20px;
				text-align: left;
			}

			:deep(p) {
				width: 30%;
				text-align: right;
				position: relative;
			}

			:deep(.button) {
				background-color: #fff;
				color: var(--body-color);
				padding: 0 20px 0 0;
				margin: 0;
				position: relative;
				right: 0;
				bottom: 0;
				display: flex;
				justify-content: flex-end;
				align-self: flex-end;

				&:hover {
					color: var(--cta-color-hover);
				}
			}

			:deep(.button)::after,
			:deep(.button)::before {
				content: '';
				width: 15px;
				height: 12px;
				background-size: contain;
				background-repeat: no-repeat;
				margin-left: 10px;
				display: inline-block;
				position: absolute;
				right: 0;
				top: 50%;
				transform: translateY(-50%);
				transition: opacity 0.3s ease;
			}

			:deep(.button)::before {
				background-image: url('~/assets/images/arrow-right.svg');
				opacity: 1;
			}

			:deep(.button)::after {
				background-image: url('~/assets/images/arrow-right-light.svg');
				opacity: 0;
			}

			:deep(.button:hover)::after {
				opacity: 1;
			}

			:deep(.button:hover)::before {
				opacity: 0;
			}
		}

		&:hover .promo-img {
			opacity: 1;
			transform: scale(1.05); // Adjust scale factor as needed
		}

		&::after {
			content: '';
			position: absolute;
			inset: 0;
			aspect-ratio: 1;
			background: rgb(0 0 0 / 60%);
			transition: all 0.3s ease-in-out;
		}

		&:hover::after {
			background: rgb(0 0 0 / 40%);
		}
	}

	.promoblock:hover .content-below :deep(.button) {
		color: var(--cta-color-hover);
	}

	.promoblock:hover .content-below :deep(.button)::after {
		opacity: 1;
	}

	.promoblock:hover .content-below :deep(.button)::before {
		opacity: 0;
	}
}

@media (max-width: 1300px) {
	.promotions {
		.promoblock {
			.header-container {
				.content {
					h2,
					h3,
					h2 a,
					h3 a {
						font-size: 40px;
					}
				}
			}

			.content-below {
				flex-flow: column;

				:deep(p:first-child) {
					width: 95%;
					margin-bottom: 0;
				}

				:deep(p) {
					width: 100%;
					text-align: right;
				}
			}
		}
	}
}

@media (max-width: 800px) {
	.promotions .promoblock {
		flex-basis: 100%;
	}
}
</style>
